import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { UsersThree, Exam, ChartLineUp, SealCheck, ArrowRight } from "@phosphor-icons/react";
import api from "../../lib/api";

function scoreColor(p) {
  if (p >= 70) return "text-[#059669] bg-[#059669]/10 border-[#059669]/20";
  if (p >= 50) return "text-accent bg-accent/10 border-accent/20";
  return "text-destructive bg-destructive/10 border-destructive/20";
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/results").then((res) => setData(res.data));
  }, []);

  if (!data) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading dashboard...</div>;

  const { summary, results, question_difficulty } = data;

  const stats = [
    { label: "Staff accounts", value: summary.staff_count, icon: UsersThree },
    { label: "Total attempts", value: summary.total_attempts, icon: Exam },
    { label: "Average score", value: `${summary.avg_score}%`, icon: ChartLineUp },
    { label: "Pass rate (\u226570%)", value: `${summary.pass_rate}%`, icon: SealCheck },
  ];

  const chartData = question_difficulty.map((q, i) => ({
    name: `Q${i + 1}`,
    accuracy: q.accuracy,
    question: q.question,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 lg:py-14">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Overview</p>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground mt-1">
            Test Results Dashboard
          </h1>
        </div>
        <Link
          to="/admin/users"
          data-testid="dashboard-manage-users"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-white font-semibold hover:-translate-y-0.5 transition-transform duration-200"
        >
          <UsersThree size={18} weight="duotone" /> Manage Staff
        </Link>
      </div>

      {/* stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            className="rounded-xl bg-white border border-border shadow-sm p-5"
            data-testid={`stat-${i}`}
          >
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <s.icon size={22} weight="duotone" className="text-primary" />
            </div>
            <p className="font-heading font-black text-3xl text-foreground mt-4">{s.value}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        {/* chart */}
        <div className="lg:col-span-2 rounded-xl bg-white border border-border shadow-sm p-6">
          <h2 className="font-heading font-bold text-lg text-foreground">Accuracy by question</h2>
          <p className="text-sm text-muted-foreground mb-4">Lower bars show topics staff struggle with most.</p>
          {summary.total_attempts === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">No test data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  content={({ active, payload }) =>
                    active && payload?.length ? (
                      <div className="bg-white border border-border rounded-lg shadow-md p-3 max-w-xs">
                        <p className="text-xs text-muted-foreground">{payload[0].payload.question}</p>
                        <p className="font-heading font-bold text-primary mt-1">{payload[0].value}% correct</p>
                      </div>
                    ) : null
                  }
                />
                <Bar dataKey="accuracy" radius={[6, 6, 0, 0]}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.accuracy >= 80 ? "#0C9AD6" : d.accuracy >= 50 ? "#F58220" : "#DC2626"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* completion */}
        <div className="rounded-xl bg-white border border-border shadow-sm p-6">
          <h2 className="font-heading font-bold text-lg text-foreground">Participation</h2>
          <p className="text-sm text-muted-foreground mb-6">Staff who have taken the test at least once.</p>
          <div className="flex items-end gap-2">
            <span className="font-heading font-black text-5xl text-primary">{summary.completed_users}</span>
            <span className="text-muted-foreground mb-2">/ {summary.staff_count} staff</span>
          </div>
          <div className="h-3 rounded-full bg-muted mt-4 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${summary.staff_count ? (summary.completed_users / summary.staff_count) * 100 : 0}%` }}
            />
          </div>
          <div className="mt-6 rounded-lg bg-muted p-4">
            <p className="text-sm text-foreground">
              <span className="font-semibold">{summary.pass_count}</span> attempt(s) passed at 70% or higher.
            </p>
          </div>
        </div>
      </div>

      {/* recent attempts */}
      <div className="rounded-xl bg-white border border-border shadow-sm mt-8 overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="font-heading font-bold text-lg text-foreground">Recent attempts</h2>
        </div>
        {results.length === 0 ? (
          <p className="text-sm text-muted-foreground p-6">No attempts recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="px-6 py-3 font-medium">Staff</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                  <th className="px-6 py-3 font-medium">Score</th>
                  <th className="px-6 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {results.slice(0, 20).map((r, i) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors duration-150" data-testid={`attempt-row-${i}`}>
                    <td className="px-6 py-3 font-medium text-foreground">{r.user_name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{r.user_email}</td>
                    <td className="px-6 py-3 text-muted-foreground">{fmt(r.submitted_at)}</td>
                    <td className="px-6 py-3">
                      <span className={`font-semibold rounded-full border px-3 py-0.5 ${scoreColor(r.percentage)}`}>
                        {r.percentage}%
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <Link to={`/admin/users/${r.user_id}`} className="text-primary hover:text-accent inline-flex items-center gap-1 transition-colors duration-200">
                        View <ArrowRight size={14} weight="bold" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
